import {
  getRequestAccessAttestationEmail,
  getSettings,
  RequestDetailsForApproval,
  signAttestationEmail,
} from "@api";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { State } from "@store";
import React from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { Modal } from "./modal";
import { Form } from "@components";

const useStyles = makeStyles({
  pointer: {
    cursor: "pointer",
  },
  title: {
    paddingTop: "4px",
  },
  table: {
    maxWidth: "1%",
    whiteSpace: "nowrap",
  },
  tableHead: {
    backgroundColor: "#414141",
    color: "white",
  },
  tableBody: {
    backgroundColor: "#F0F0F0",
    "& li": {
      listStyleType: "none",
      paddingRight: "35px",
    },
    "& th, & td": {
      borderBottom: 0,
      textAlign: "left",
    },
    "& span": {
      fontWeight: "bold",
    },
  },
  userAgreement: {
    backgroundColor: "#F0F0F0",
    borderLeftColor: "#149DEB",
    borderLeftStyle: "solid",
    borderLeftWidth: "10px",
  },
  buttonSubmit: {
    marginTop: "50px",
    "& button": {
      float: "right",
      marginLeft: "25px",
    },
  },
  link: {
    fontFamily: "Arial",
    color: "#0f7dca",
    cursor: "pointer",
  },
});

export const RequestAccessAttestationEmail: React.FC = () => {
  const classNames = useStyles();
  const history = useHistory();
  const { encryptId } = useParams();
  const [isAttesting, setIsAttesting] = React.useState(false);
  const user = useSelector((state: State) => {
    return state.user;
  });
  const [data, setData] = React.useState<{
    details: RequestDetailsForApproval | null;
    fetching: boolean;
    settings: string | null;
  }>({
    details: null,
    fetching: false,
    settings: null,
  });
  const [modal, setModal] = React.useState<{
    open: boolean;
    status: "accepted" | "declined" | undefined;
  }>({
    open: false,
    status: undefined,
  });
  const form = Form.useManager<{
    name: string | undefined;
    status: "accept" | "decline" | undefined;
  }>({
    name: undefined,
    status: undefined,
  });

  React.useEffect(() => {
    setData({
      details: null,
      fetching: true,
      settings: null,
    });
    getRequestAccessAttestationEmail(encryptId).then(({ request }) => {
      getSettings().then((settings) => {
        setData({
          details: request,
          fetching: false,
          settings: request.isMobileAttestation
            ? settings.attestationMobile
            : settings.attestation,
        });
      });
    });
  }, [encryptId]);

  function onFormValueChange(name: keyof typeof form.values, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<
    [keyof typeof form.values, Error?]
  > {
    switch (name) {
      case "name":
        return Promise.resolve([
          name as keyof typeof form.values,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEqual(value, user?.name),
        ]);
      default:
        return Promise.resolve([name as keyof typeof form.values]);
    }
  }

  function submit(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);

        if (valid) {
          if (form.values.status === undefined) {
            setModal({
              open: true,
              status: undefined,
            });
          } else if (data.details) {
            setIsAttesting(true);
            signAttestationEmail(
              data.details.id,
              form.values.status === "accept",
              form.values.name || ""
            ).then(() => {
              setIsAttesting(false);
              setModal({
                open: true,
                status:
                  form.values.status === "accept"
                    ? "accepted"
                    : form.values.status === "decline"
                    ? "declined"
                    : undefined,
              });
            }).finally(() => {
              setIsAttesting(false);
            });
          }
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  return data.fetching ? (
    <Box display="flex" alignItems="center" justifyContent="center" p={8}>
      <CircularProgress />
    </Box>
  ) : data.details && data.settings ? (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className={classNames.link}>
            <Typography
              variant="body1"
              className={classNames.link}
              onClick={() => history.goBack()}
            >
              &larr; Back to List
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography className={classNames.title}>
            Attestation {data.details.referenceNumber}
          </Typography>
          <Box pt={2}>
            <TableContainer>
              <Table className={classNames.table}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      variant="head"
                      align="left"
                      className={classNames.tableHead}
                    >
                      Request Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell className={classNames.tableBody}>
                      <li>
                        <span>Reference No: </span>
                        <Typography
                          variant="body1"
                          color="primary"
                          className={classNames.pointer}
                          onClick={() =>
                            history.push(`/dashboard/requests/${encryptId}`)
                          }
                        >
                          {data.details.referenceNumber}
                        </Typography>
                      </li>
                      <li>
                        <span>Type of Action: </span>
                        {data.details.requestType}
                      </li>
                      <li>
                        <span>Role: </span>
                        {data.details.role}
                      </li>
                    </TableCell>
                    <TableCell className={classNames.tableBody}>
                      <li>
                        <span>Environment: </span>
                        {data.details.environment}
                      </li>
                      <li>
                        <span>System Category: </span>
                        {data.details.category}
                      </li>
                      <li>
                        <span>System Name: </span>
                        {data.details.subCategory}
                      </li>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box p={5} className={classNames.userAgreement}>
            <div dangerouslySetInnerHTML={{ __html: data.settings }} />
            <Form>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" height="72px" pt={3}>
                  <Box fontSize="h4.fontSize" fontFamily="'Arizonia', cursive">
                    {form.values.name}
                  </Box>
                </Box>
              </Grid>
              <Form.Textbox
                error={
                  form.errors.name &&
                  form.errors.name.message.toUpperCase() === "REQUIRED"
                    ? form.errors.name
                    : undefined
                }
                label="Please input your name here"
                name="name"
                onChange={onFormValueChange}
                placeholder={user?.name}
                size={6}
                value={form.values.name}
                disabled={false}
              />
              <Form.Radiogroup
                label="&nbsp;"
                name="status"
                onChange={onFormValueChange}
                options={[
                  {
                    value: "accept",
                    label: "Accept",
                  },
                  {
                    value: "decline",
                    label: "Decline",
                  },
                ]}
                row
                size={6}
                value={form.values.status || ""}
                disabled
              />
              <Grid item xs={12} component={Box} height="35px">
                {form.errors.name &&
                form.errors.name.message.toUpperCase() !== "REQUIRED" ? (
                  <FormHelperText error>
                    <Typography
                      variant="caption"
                      style={{ textTransform: "none" }}
                    >
                      {form.errors.name.message}
                    </Typography>
                  </FormHelperText>
                ) : null}
              </Grid>
            </Form>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box p={2} mb={4} className={classNames.buttonSubmit}>
            <Button variant="contained" color="primary" onClick={submit} disabled={isAttesting}>
              Submit
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Modal
        onClose={() => {
          setModal({
            ...modal,
            open: false,
          });
        }}
        open={modal.open}
        status={modal.status}
      />
    </React.Fragment>
  ) : null;
};
